
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { SEO } from '@/components/SEO';
import { format } from 'date-fns';
import { Calendar, User, Clock, ArrowLeft, Share2 } from 'lucide-react';
import NotFound from './NotFound';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import RecentPosts from '@/components/blog/RecentPosts';
import BlogRelatedGames from '@/components/blog/BlogRelatedGames';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import SharePopoverContent from '@/components/game-detail/SharePopoverContent';
import AdDisplay from '@/components/ads/AdDisplay';

const BlogPostPage = () => {
  const { postSlug } = useParams<{ postSlug: string }>();

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blog_post', postSlug],
    queryFn: async () => {
      if (!postSlug) return null;
      
      // Decode the URL slug to handle URL-encoded characters
      const decodedSlug = decodeURIComponent(postSlug);
      console.log('Looking for blog post with slug:', decodedSlug);
      
      // Try multiple slug variations to find the post
      const slugVariations = [
        decodedSlug,
        postSlug, // original encoded version
        decodedSlug.toLowerCase(),
        decodedSlug.replace(/\s+/g, '-'),
        decodedSlug.replace(/\s+/g, '_'),
        decodedSlug.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-'),
      ];
      
      console.log('Trying slug variations:', slugVariations);
      
      let data = null;
      
      // Try each slug variation
      for (const slug of slugVariations) {
        console.log('Trying slug:', slug);
        
        const { data: result, error: queryError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('slug', slug)
          .eq('status', 'published')
          .maybeSingle();
        
        if (queryError) {
          console.warn('Query error for slug', slug, ':', queryError);
          continue;
        }
        
        if (result) {
          console.log('Found post with slug:', slug);
          data = result;
          break;
        }
      }
      
      // If still no match, try partial matching with title
      if (!data) {
        console.log('No exact match found, trying title search');
        
        const searchTerm = decodedSlug.replace(/[-_]/g, ' ');
        console.log('Searching by title with term:', searchTerm);
        
        const { data: titleResult, error: titleError } = await supabase
          .from('blog_posts')
          .select('*')
          .ilike('title', `%${searchTerm}%`)
          .eq('status', 'published')
          .limit(1)
          .maybeSingle();
        
        if (titleError) {
          console.warn('Title search error:', titleError);
        } else if (titleResult) {
          console.log('Found post by title search:', titleResult.title);
          data = titleResult;
        }
      }
      
      if (!data) {
        console.error('Blog post not found for slug:', postSlug);
        throw new Error('Blog post not found');
      }
      
      return data;
    },
    enabled: !!postSlug,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          {/* Navigation Skeleton */}
          <Skeleton className="h-10 w-32 mb-8" />
          
          {/* Header Skeleton */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-8">
            <Skeleton className="h-12 md:h-16 w-3/4 mb-6" />
            <div className="flex flex-wrap gap-6 mb-8">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-20" />
            </div>
            <Skeleton className="h-64 md:h-96 w-full rounded-xl" />
          </div>
          
          {/* Content Skeleton */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md mx-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ArrowLeft className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Post</h3>
          <p className="text-red-500 mb-6">{error.message}</p>
          <Button asChild variant="outline">
            <Link to="/blog">Back to Blog</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!post) {
    return <NotFound />;
  }

  const readingTime = Math.ceil((post.content?.length || 0) / 1000);

  return (
    <>
      <SEO
        pageType="blog_post"
        pageIdentifier={post.slug}
        title={post.meta_title || post.title}
        description={post.meta_description || post.content?.substring(0, 160)}
        keywords={post.meta_keywords || undefined}
        image={post.featured_image_url || undefined}
        type="article"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          {/* Navigation */}
          <div className="mb-8">
            <Button 
              asChild 
              variant="ghost" 
              size="sm"
              className="text-gray-600 hover:text-gray-900 hover:bg-white/80 backdrop-blur-sm"
            >
              <Link to="/blog" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Blog
              </Link>
            </Button>
          </div>

          {/* Article Header */}
          <article className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
            <div className="p-8 md:p-12">
              <div className="space-y-6">
                {/* Meta Information */}
                <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                  {post.author_name && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-md">
                        <User className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{post.author_name}</p>
                        <p className="text-xs text-gray-500">Author</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-6">
                    {post.published_at && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <time dateTime={post.published_at} className="font-medium">
                          {format(new Date(post.published_at), 'MMMM dd, yyyy')}
                        </time>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">{readingTime} min read</span>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  {post.title}
                </h1>

                {/* Share Button */}
                <div className="flex items-center gap-3 pt-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="flex items-center gap-2">
                        <Share2 className="h-4 w-4" />
                        <span className="hidden sm:inline">Share</span>
                      </Button>
                    </PopoverTrigger>
                    <SharePopoverContent gameTitle={post.title} />
                  </Popover>
                </div>
              </div>

              {/* Featured Image */}
              {post.featured_image_url && (
                <div className="mt-8 -mx-8 md:-mx-12 relative">
                  <div className="aspect-video md:aspect-[2/1] overflow-hidden">
                    <img 
                      src={post.featured_image_url} 
                      alt={post.title} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
                  </div>
                </div>
              )}
            </div>
          </article>

          {/* Ad Above Post Content */}
          <AdDisplay position="blog-above-post" className="mb-8" />

          {/* Article Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12">
            <div className="prose prose-lg prose-gray max-w-none 
                         prose-headings:font-bold prose-headings:text-gray-900 prose-headings:tracking-tight
                         prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl
                         prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                         prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-a:font-medium
                         prose-strong:text-gray-900 prose-strong:font-semibold
                         prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
                         prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:bg-blue-50 
                         prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:not-italic prose-blockquote:rounded-r-lg
                         prose-ul:space-y-2 prose-ol:space-y-2 prose-li:text-gray-700
                         prose-img:rounded-xl prose-img:shadow-lg">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {post.content || ''}
              </ReactMarkdown>
            </div>
          </div>

          {/* Related Games Section */}
          <div className="mb-12">
            <BlogRelatedGames postId={post.id} />
          </div>

          {/* Recent Posts Section */}
          <div>
            <RecentPosts currentPostId={post.id} />
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPostPage;
